import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, MapPin, Building2, LogOut, Trash2, Edit, Plus, Calendar, AlertTriangle, BarChart3, DollarSign, Wrench } from "lucide-react";
import { MaintenancePanel } from "@/components/MaintenancePanel";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [imports, setImports] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditLocation, setShowEditLocation] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedResetRequest, setSelectedResetRequest] = useState(null);
  
  // Corporate Overhead state
  const [corpOverheadConfigs, setCorpOverheadConfigs] = useState([]);
  const [corpOverheadPeriod, setCorpOverheadPeriod] = useState("");
  const [corpOverheadAmount, setCorpOverheadAmount] = useState("");
  const [savingCorpOverhead, setSavingCorpOverhead] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    full_name: "",
    assigned_locations: []
  });
  
  const [locationEdit, setLocationEdit] = useState({ name: "" });
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    loadData();
    loadCorpOverheadConfig();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load each API endpoint individually to prevent one failure from blocking others
      const apiCalls = [
        { name: 'users', call: () => axios.get(`${API}/admin/users`) },
        { name: 'locations', call: () => axios.get(`${API}/admin/locations`) },
        { name: 'imports', call: () => axios.get(`${API}/admin/data/imports`) },
        { name: 'periods', call: () => axios.get(`${API}/admin/data/periods`) },
        { name: 'passwordResets', call: () => axios.get(`${API}/admin/password-reset-requests`) }
      ];

      const results = await Promise.allSettled(apiCalls.map(api => api.call()));
      
      results.forEach((result, index) => {
        const apiName = apiCalls[index].name;
        if (result.status === 'fulfilled') {
          switch (apiName) {
            case 'users':
              setUsers(result.value.data);
              break;
            case 'locations':
              setLocations(result.value.data);
              break;
            case 'imports':
              setImports(result.value.data.imports || []);
              break;
            case 'periods':
              setPeriods(result.value.data.periods || []);
              break;
            case 'passwordResets':
              setPasswordResetRequests(result.value.data.requests);
              break;
          }
        } else {
          console.error(`Failed to load ${apiName}:`, result.reason);
          if (apiName === 'passwordResets') {
            // Set empty array for password resets if it fails
            setPasswordResetRequests([]);
          }
        }
      });
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadCorpOverheadConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/corporate-overhead`);
      setCorpOverheadConfigs(response.data);
    } catch (error) {
      console.error("Error loading corporate overhead config:", error);
    }
  };

  const handleSaveCorpOverhead = async (e) => {
    e.preventDefault();
    setSavingCorpOverhead(true);
    
    try {
      const amount = parseFloat(corpOverheadAmount);
      if (isNaN(amount) || amount < 0) {
        toast.error("Please enter a valid positive number");
        setSavingCorpOverhead(false);
        return;
      }
      
      if (!corpOverheadPeriod) {
        toast.error("Please enter a period (e.g., 2025-06)");
        setSavingCorpOverhead(false);
        return;
      }
      
      if (editingPeriod) {
        // Update existing
        await axios.put(`${API}/config/corporate-overhead/${editingPeriod}`, {
          total_amount: amount
        });
        toast.success(`Corporate Overhead for ${editingPeriod} updated successfully`);
        setEditingPeriod(null);
      } else {
        // Create new
        await axios.post(`${API}/config/corporate-overhead`, {
          period: corpOverheadPeriod,
          total_amount: amount
        });
        toast.success(`Corporate Overhead for ${corpOverheadPeriod} created successfully`);
      }
      
      setCorpOverheadPeriod("");
      setCorpOverheadAmount("");
      await loadCorpOverheadConfig();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to save Corporate Overhead");
      }
      console.error("Error saving corporate overhead:", error);
    } finally {
      setSavingCorpOverhead(false);
    }
  };

  const handleEditCorpOverhead = (config) => {
    setEditingPeriod(config.period);
    setCorpOverheadPeriod(config.period);
    setCorpOverheadAmount(config.total_amount.toString());
  };

  const handleDeleteCorpOverhead = async (period) => {
    if (!window.confirm(`Delete Corporate Overhead for ${period}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/config/corporate-overhead/${period}`);
      toast.success(`Corporate Overhead for ${period} deleted successfully`);
      await loadCorpOverheadConfig();
    } catch (error) {
      toast.error("Failed to delete Corporate Overhead");
      console.error("Error deleting corporate overhead:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingPeriod(null);
    setCorpOverheadPeriod("");
    setCorpOverheadAmount("");
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success("User created successfully");
      setShowCreateUser(false);
      setNewUser({ email: "", password: "", full_name: "", assigned_locations: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleToggleLocation = (locationName) => {
    setNewUser(prev => ({
      ...prev,
      assigned_locations: prev.assigned_locations.includes(locationName)
        ? prev.assigned_locations.filter(l => l !== locationName)
        : [...prev.assigned_locations, locationName]
    }));
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/locations/${selectedLocation}`, locationEdit);
      toast.success("Location updated successfully");
      setShowEditLocation(false);
      loadData();
    } catch (error) {
      toast.error("Failed to update location");
    }
  };

  const handleDeleteLocation = async (locationName) => {
    if (!window.confirm(`Are you sure you want to delete "${locationName}" and all its data?`)) return;
    try {
      await axios.delete(`${API}/admin/locations/${locationName}`);
      toast.success("Location deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  const handleDeleteImport = async (batchId) => {
    if (!window.confirm("Are you sure you want to delete this import? This will remove all associated data.")) return;
    try {
      await axios.delete(`${API}/admin/data/import/${batchId}`);
      toast.success("Import deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete import");
    }
  };

  const handleClearAllData = async () => {
    if (!window.confirm("⚠️ WARNING: This will delete ALL financial data from the system. Are you absolutely sure?")) return;
    if (!window.confirm("This action CANNOT be undone. Click OK to proceed with nuclear data deletion.")) return;
    try {
      const response = await axios.delete(`${API}/admin/data/clear-all`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear data");
    }
  };

  const handleDeletePeriod = async () => {
    if (!selectedPeriod) {
      toast.error("Please select a period to delete");
      return;
    }
    
    const selectedPeriodData = periods.find(p => p.period === selectedPeriod);
    const confirmMessage = `⚠️ WARNING: Delete ALL data for ${selectedPeriod}?\n\nThis will remove:\n- ${selectedPeriodData?.record_count || 0} line items\n- Data from ${selectedPeriodData?.location_count || 0} locations\n- Related YTD calculations will be recalculated\n\nThis action CANNOT be undone!`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const response = await axios.delete(`${API}/admin/data/period/${encodeURIComponent(selectedPeriod)}`);
      toast.success(response.data.message);
      setSelectedPeriod("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete period data");
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!selectedResetRequest || !newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    try {
      await axios.post(`${API}/admin/reset-user-password`, {
        user_id: selectedResetRequest.user_id,
        new_password: newPassword
      });
      
      toast.success(`Password reset successfully for ${selectedResetRequest.email}`);
      setShowPasswordReset(false);
      setNewPassword("");
      setSelectedResetRequest(null);
      loadData(); // Refresh to remove completed requests
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-slate-700" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Admin Panel</h1>
                <p className="text-sm text-slate-600">Manage users and locations</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-800">{user?.full_name}</p>
                <p className="text-xs text-slate-500">Administrator</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = "/dashboard"}
                  className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  <BarChart3 className="h-4 w-4" />
                  Dashboard
                </Button>
                <Button variant="outline" onClick={onLogout} className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="locations" className="gap-2">
              <MapPin className="h-4 w-4" />
              Locations
            </TabsTrigger>
            <TabsTrigger value="corporate-overhead" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Corporate Overhead
            </TabsTrigger>
            <TabsTrigger value="data" className="gap-2">
              <Trash2 className="h-4 w-4" />
              Data Management
            </TabsTrigger>
            <TabsTrigger value="password-resets" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Password Resets
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="gap-2" data-testid="maintenance-tab">
              <Wrench className="h-4 w-4" />
              Maintenance
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-800">User Management</CardTitle>
                    <CardDescription>Create and manage user accounts</CardDescription>
                  </div>
                  <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                    <DialogTrigger asChild>
                      <Button className="bg-slate-800 hover:bg-slate-900 gap-2">
                        <Plus className="h-4 w-4" />
                        Create User
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New User</DialogTitle>
                        <DialogDescription>Add a new user and assign locations</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Full Name</Label>
                          <Input
                            value={newUser.full_name}
                            onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Temporary Password</Label>
                          <Input
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Assign Locations</Label>
                          <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                            {locations.length === 0 ? (
                              <p className="text-sm text-slate-500">No locations available yet</p>
                            ) : (
                              locations.map((loc) => (
                                <div key={loc.name} className="flex items-center space-x-2">
                                  <Checkbox
                                    checked={newUser.assigned_locations.includes(loc.name)}
                                    onCheckedChange={() => handleToggleLocation(loc.name)}
                                  />
                                  <label className="text-sm">{loc.name}</label>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-slate-800 hover:bg-slate-900">
                          Create User
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <p className="text-center text-slate-500">Loading...</p>
                ) : users.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No users yet</p>
                ) : (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{u.full_name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Locations: {u.assigned_locations.length === 0 ? "None" : u.assigned_locations.join(", ")}
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteUser(u.id)}
                          className="gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <CardTitle className="text-slate-800">Location Management</CardTitle>
                <CardDescription>View and manage locations (auto-created from uploaded data)</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <p className="text-center text-slate-500">Loading...</p>
                ) : locations.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No locations yet. Upload data to create locations.</p>
                ) : (
                  <div className="space-y-3">
                    {locations.map((loc) => (
                      <div
                        key={loc.name}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{loc.name}</p>
                          <p className="text-sm text-slate-500">{loc.record_count} financial records</p>
                        </div>
                        <div className="flex gap-2">
                          <Dialog open={showEditLocation && selectedLocation === loc.name} onOpenChange={(open) => {
                            setShowEditLocation(open);
                            if (!open) setSelectedLocation(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedLocation(loc.name);
                                  setLocationEdit({ name: loc.name });
                                }}
                                className="gap-2"
                              >
                                <Edit className="h-4 w-4" />
                                Rename
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Rename Location</DialogTitle>
                                <DialogDescription>Update the location name</DialogDescription>
                              </DialogHeader>
                              <form onSubmit={handleUpdateLocation} className="space-y-4">
                                <div className="space-y-2">
                                  <Label>Location Name</Label>
                                  <Input
                                    value={locationEdit.name}
                                    onChange={(e) => setLocationEdit({ name: e.target.value })}
                                    required
                                  />
                                </div>
                                <Button type="submit" className="w-full bg-slate-800 hover:bg-slate-900">
                                  Update Location
                                </Button>
                              </form>
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteLocation(loc.name)}
                            className="gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Corporate Overhead Tab */}
          <TabsContent value="corporate-overhead">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-green-50 border-b border-green-200">
                <CardTitle className="text-green-800 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Corporate Overhead by Period
                </CardTitle>
                <CardDescription>
                  Set Corporate Overhead amounts for each month/period. Each amount is automatically split evenly across all locations.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                
                {/* Existing Corporate Overhead List */}
                {corpOverheadConfigs.length > 0 && (
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Existing Corporate Overhead by Period</h3>
                    <div className="space-y-2">
                      {corpOverheadConfigs.map((config) => (
                        <div key={config.period} className="flex items-center justify-between bg-white p-3 rounded border border-slate-200">
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">{config.period}</div>
                            <div className="text-sm text-slate-600">
                              Total: <span className="font-semibold">${config.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              {' '} | Per Location: <span className="font-semibold text-green-600">${config.allocation_per_location?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              {' '} | Locations: {config.locations_count}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditCorpOverhead(config)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteCorpOverhead(config.period)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Add/Edit Form */}
                <form onSubmit={handleSaveCorpOverhead} className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="text-sm font-semibold text-blue-800 mb-3">
                      {editingPeriod ? `Edit Corporate Overhead for ${editingPeriod}` : 'Add New Corporate Overhead'}
                    </h3>
                    
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="corp-overhead-period" className="text-sm font-medium text-slate-700">
                          Period (e.g., 2025-06)
                        </Label>
                        <Input
                          id="corp-overhead-period"
                          type="text"
                          value={corpOverheadPeriod}
                          onChange={(e) => setCorpOverheadPeriod(e.target.value)}
                          placeholder="2025-06"
                          className="mt-1"
                          disabled={!!editingPeriod}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="corp-overhead-amount" className="text-sm font-medium text-slate-700">
                          Total Corporate Overhead Amount ($)
                        </Label>
                        <Input
                          id="corp-overhead-amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={corpOverheadAmount}
                          onChange={(e) => setCorpOverheadAmount(e.target.value)}
                          placeholder="Enter total amount (e.g., 39727.67)"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        type="submit"
                        disabled={savingCorpOverhead}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        {savingCorpOverhead ? "Saving..." : (editingPeriod ? "Update" : "Add Corporate Overhead")}
                      </Button>
                      {editingPeriod && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">How it works:</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>Enter Corporate Overhead for each month/period separately</li>
                    <li>The system automatically divides it equally among all locations</li>
                    <li>Each location receives: Total Amount ÷ Number of Locations</li>
                    <li>Dashboard uses period-specific Corporate OH for accurate calculations</li>
                    <li>You can edit or delete any period's Corporate OH</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Management Tab */}
          <TabsContent value="data">
            {/* Delete Specific Period */}
            <Card className="border-slate-200 shadow-sm mb-6">
              <CardHeader className="bg-amber-50 border-b border-amber-200">
                <CardTitle className="text-amber-800 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Delete Specific Month Data
                </CardTitle>
                <CardDescription>Remove data from a specific period/month and recalculate YTD</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <Label htmlFor="period-select" className="text-sm font-medium text-slate-700 mb-2 block">
                      Select Period to Delete
                    </Label>
                    <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a period..." />
                      </SelectTrigger>
                      <SelectContent>
                        {periods.length === 0 ? (
                          <SelectItem value="no-data" disabled>No data periods available</SelectItem>
                        ) : (
                          periods.map((period) => (
                            <SelectItem key={period.period} value={period.period}>
                              {period.period} ({period.record_count} items, {period.location_count} locations)
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-6">
                    <Button
                      variant="destructive"
                      onClick={handleDeletePeriod}
                      disabled={!selectedPeriod || selectedPeriod === "no-data"}
                      className="gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Period
                    </Button>
                  </div>
                </div>
                
                {selectedPeriod && selectedPeriod !== "no-data" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          Selected: {selectedPeriod}
                        </p>
                        {periods.find(p => p.period === selectedPeriod) && (
                          <p className="text-xs text-amber-700 mt-1">
                            Will delete {periods.find(p => p.period === selectedPeriod).record_count} line items 
                            from {periods.find(p => p.period === selectedPeriod).location_count} locations. 
                            YTD calculations will be automatically recalculated.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Nuclear Option - Delete All Data */}
            <Card className="border-slate-200 shadow-sm mb-6">
              <CardHeader className="bg-rose-50 border-b border-rose-200">
                <CardTitle className="text-rose-800 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Nuclear Option - Delete All Data
                </CardTitle>
                <CardDescription>⚠️ Permanently delete ALL financial data from the entire system</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Button
                  variant="destructive"
                  onClick={handleClearAllData}
                  className="gap-2 bg-rose-600 hover:bg-rose-700"
                >
                  <Trash2 className="h-4 w-4" />
                  🗑️ DELETE EVERYTHING
                </Button>
                <p className="text-sm text-slate-600 mt-2">
                  This will delete ALL financial records, P&L data, AND YTD calculations from the entire system. Use with extreme caution.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <CardTitle className="text-slate-800">Import History</CardTitle>
                <CardDescription>View and delete specific data imports</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? (
                  <p className="text-center text-slate-500">Loading...</p>
                ) : imports.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No imports yet</p>
                ) : (
                  <div className="space-y-3">
                    {imports.map((imp) => (
                      <div
                        key={imp.batch_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">
                            Import: {imp.period || 'Unknown period'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(imp.created_at).toLocaleString()} • {imp.record_count} line items
                          </p>
                          <p className="text-xs text-slate-400 font-mono mt-1">
                            Batch ID: {imp.batch_id}
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteImport(imp.batch_id)}
                          className="gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Password Reset Requests Tab */}
          <TabsContent value="password-resets">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <CardTitle className="text-slate-800">Password Reset Requests</CardTitle>
                <CardDescription>Manage user password reset requests</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {passwordResetRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No pending password reset requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {passwordResetRequests.map((request) => (
                      <div key={request.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            <div>
                              <p className="font-medium text-slate-800">{request.email}</p>
                              <p className="text-sm text-slate-500">
                                Requested: {new Date(request.requested_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        <Dialog open={showPasswordReset && selectedResetRequest?.id === request.id} 
                               onOpenChange={(open) => {
                                 setShowPasswordReset(open);
                                 if (!open) {
                                   setSelectedResetRequest(null);
                                   setNewPassword("");
                                 }
                               }}>
                          <DialogTrigger asChild>
                            <Button
                              onClick={() => setSelectedResetRequest(request)}
                              className="bg-slate-800 hover:bg-slate-900 gap-2"
                            >
                              <Edit className="h-4 w-4" />
                              Reset Password
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Reset Password for {request.email}</DialogTitle>
                              <DialogDescription>
                                Enter a new password for this user. They will need to use this password to log in.
                              </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handlePasswordReset} className="space-y-4">
                              <div className="space-y-2">
                                <Label>New Password</Label>
                                <Input
                                  type="password"
                                  placeholder="Enter new password"
                                  value={newPassword}
                                  onChange={(e) => setNewPassword(e.target.value)}
                                  required
                                  minLength="6"
                                />
                                <p className="text-xs text-slate-500">Minimum 6 characters</p>
                              </div>
                              <div className="flex gap-2">
                                <Button type="submit" className="flex-1 bg-slate-800 hover:bg-slate-900">
                                  Reset Password
                                </Button>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  onClick={() => {
                                    setShowPasswordReset(false);
                                    setSelectedResetRequest(null);
                                    setNewPassword("");
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="maintenance">
            <MaintenancePanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
